import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Team } from "@/types";

/**
 * Team colors available for customization
 */
const TEAM_COLORS = [
  { name: "Rood", hex: "#EF4444" },
  { name: "Oranje", hex: "#F97316" },
  { name: "Amber", hex: "#F59E0B" },
  { name: "Geel", hex: "#EAB308" },
  { name: "Lime", hex: "#84CC16" },
  { name: "Groen", hex: "#22C55E" },
  { name: "Teal", hex: "#14B8A6" },
  { name: "Cyaan", hex: "#06B6D4" },
  { name: "Blauw", hex: "#3B82F6" },
  { name: "Indigo", hex: "#6366F1" },
  { name: "Paars", hex: "#8B5CF6" },
  { name: "Roze", hex: "#EC4899" },
  { name: "Grijs", hex: "#6B7280" },
  { name: "Zwart", hex: "#1F2937" },
];

/**
 * Team Customize Screen - Simplified (avatar editing moved to Avatar Studio)
 */
export default function TeamCustomizeScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const colors = useThemeColors();

  const [team, setTeam] = useState<Team | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedColor, setSelectedColor] = useState(TEAM_COLORS[0].hex);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Fetch team data
   */
  const fetchTeam = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("teams")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setTeam(data);
      setName(data.name);
      setDescription(data.description || "");
      setSelectedColor(data.color || TEAM_COLORS[0].hex);
      setAvatarUrl(data.avatar_url);
    } catch (error) {
      console.error("Error fetching team:", error);
      Alert.alert("Fout", "Kon team niet laden", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  // Initial load
  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Refresh when screen comes into focus (e.g., returning from Avatar Studio)
  useFocusEffect(
    useCallback(() => {
      if (team) {
        // Re-fetch to get updated avatar
        fetchTeam();
      }
    }, [team?.id])
  );

  /**
   * Open Avatar Studio
   */
  const handleOpenAvatarStudio = () => {
    if (!team) return;
    router.push({
      pathname: "/(tabs)/teams/avatar-creator",
      params: {
        id: team.id,
        weekendId: team.weekend_id,
        color: selectedColor,
      },
    } as any);
  };

  /**
   * Save changes (name, description, color only - avatar is saved in Avatar Studio)
   */
  const handleSave = async () => {
    if (!team || !user) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      Alert.alert("Fout", "Vul een teamnaam in");
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("teams")
        .update({
          name: trimmedName,
          description: description.trim() || null,
          color: selectedColor,
        })
        .eq("id", team.id);

      if (error) throw error;

      Alert.alert("Opgeslagen! 🎨", "Je team is bijgewerkt", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (error: any) {
      console.error("Error saving team:", error);
      Alert.alert("Fout", error.message || "Kon team niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!team) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-8"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="alert-circle-outline" size={64} color={colors.border} />
        <Text
          className="text-xl font-semibold mt-4"
          style={{ color: colors.text }}
        >
          Team niet gevonden
        </Text>
        <Pressable
          onPress={() => router.back()}
          className="mt-6 rounded-xl px-6 py-3"
          style={{ backgroundColor: colors.primaryLight }}
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Text className="font-medium" style={{ color: colors.primary }}>
            Terug
          </Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  // Check if avatar is URL or emoji
  const isAvatarUrl = avatarUrl && avatarUrl.startsWith("http");
  const isAvatarEmoji = avatarUrl && !avatarUrl.startsWith("http");

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "bottom"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
      >
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 40 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <Pressable
              onPress={() => router.back()}
              className="p-2 -ml-2"
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="arrow-back" size={24} color={colors.text} />
            </Pressable>
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              Team Aanpassen
            </Text>
            <Pressable
              onPress={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-xl"
              style={{
                backgroundColor: isSaving ? colors.border : colors.primary,
              }}
              accessibilityRole="button"
              accessibilityLabel="Opslaan"
            >
              {isSaving ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text className="text-white font-semibold">Opslaan</Text>
              )}
            </Pressable>
          </View>

          {/* Avatar Preview Section */}
          <View
            className="items-center py-6 px-4"
            style={{ backgroundColor: colors.surface }}
          >
            {/* Avatar Display */}
            <View
              className="w-28 h-28 rounded-full items-center justify-center border-4 overflow-hidden shadow-lg"
              style={{
                borderColor: selectedColor,
                backgroundColor: isAvatarUrl ? "transparent" : `${selectedColor}15`,
              }}
            >
              {isAvatarUrl ? (
                <Image
                  source={{ uri: avatarUrl }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
              ) : isAvatarEmoji ? (
                <Text className="text-5xl">{avatarUrl}</Text>
              ) : (
                <Text
                  className="text-4xl font-bold"
                  style={{ color: selectedColor }}
                >
                  {name.trim().charAt(0).toUpperCase() || "T"}
                </Text>
              )}
            </View>

            {/* Team Preview */}
            <Text
              className="text-xl font-bold mt-3"
              style={{ color: selectedColor }}
            >
              {name.trim() || "Team Naam"}
            </Text>
            {description.trim() && (
              <Text
                className="italic mt-1 text-center"
                style={{ color: colors.textSecondary }}
              >
                "{description.trim()}"
              </Text>
            )}
          </View>

          {/* Avatar Studio Card */}
          <View className="mx-4 mt-4">
            <Pressable
              onPress={handleOpenAvatarStudio}
              className="rounded-2xl p-5 active:opacity-90"
              style={{ backgroundColor: colors.primary }}
              accessibilityRole="button"
              accessibilityLabel="Open Avatar Studio"
            >
              <View className="flex-row items-center">
                <View
                  className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                  style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  <Ionicons name="color-palette" size={28} color="#FFFFFF" />
                </View>
                <View className="flex-1">
                  <Text className="text-white text-lg font-bold">
                    Avatar Studio 🎨
                  </Text>
                  <Text className="text-white/80 text-sm mt-1">
                    Maak een unieke avatar met stickers, tekst en foto's!
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={24} color="#FFFFFF" />
              </View>
            </Pressable>

            {/* Tip */}
            <View className="flex-row items-start mt-3 px-2">
              <Ionicons name="bulb-outline" size={16} color={colors.textTertiary} />
              <Text className="text-xs ml-2 flex-1" style={{ color: colors.textTertiary }}>
                Tip: In de Avatar Studio kun je emoji's, tekst en foto's
                toevoegen en verplaatsen. Knijp met twee vingers om te schalen!
              </Text>
            </View>
          </View>

          {/* Team Name */}
          <View className="px-4 mt-6">
            <Text className="text-base font-semibold mb-2" style={{ color: colors.text }}>
              Team Naam
            </Text>
            <TextInput
              className="rounded-xl px-4 py-3 text-base"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
                color: colors.text,
              }}
              value={name}
              onChangeText={setName}
              placeholder="Voer teamnaam in"
              placeholderTextColor={colors.textTertiary}
              maxLength={30}
            />
          </View>

          {/* Team Slogan */}
          <View className="px-4 mt-5">
            <Text className="text-base font-semibold mb-2" style={{ color: colors.text }}>
              Team Slogan{" "}
              <Text style={{ color: colors.textTertiary, fontWeight: "normal" }}>
                (optioneel)
              </Text>
            </Text>
            <TextInput
              className="rounded-xl px-4 py-3 text-base"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
                color: colors.text,
              }}
              value={description}
              onChangeText={setDescription}
              placeholder='Bijv. "Samen sterk!"'
              placeholderTextColor={colors.textTertiary}
              maxLength={50}
            />
          </View>

          {/* Team Color */}
          <View className="px-4 mt-5">
            <Text className="text-base font-semibold mb-3" style={{ color: colors.text }}>
              Team Kleur
            </Text>
            <View className="flex-row flex-wrap">
              {TEAM_COLORS.map((color) => (
                <Pressable
                  key={color.hex}
                  onPress={() => setSelectedColor(color.hex)}
                  className="mr-2 mb-2"
                  accessibilityLabel={`Selecteer ${color.name}`}
                  accessibilityRole="button"
                >
                  <View
                    className="w-11 h-11 rounded-full items-center justify-center"
                    style={{
                      backgroundColor: color.hex,
                      borderWidth: selectedColor === color.hex ? 3 : 0,
                      borderColor: colors.text,
                    }}
                  >
                    {selectedColor === color.hex && (
                      <Ionicons name="checkmark" size={22} color="#FFFFFF" />
                    )}
                  </View>
                </Pressable>
              ))}
            </View>
          </View>

          {/* Spacer */}
          <View className="h-6" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
